import { ReviewForm } from '@/components/reviews/ReviewForm'
import { ReviewList } from '@/components/reviews/ReviewList'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { Review } from '@/types/api'
import { X } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

export default function ReviewsPage() {
  const { toast } = useToast()
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [selectedReview, setSelectedReview] = useState<Review | undefined>(undefined)

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiClient.getReviews({
        page,
        per_page: 10,
        search,
      })
      setReviews(response.data || [])
      // Verify pagination structure matches admin API response
      if (response.meta) {
        setTotalPages(response.meta.last_page)
        setTotalItems(response.meta.total)
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to fetch reviews',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [page, search, toast])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReviews()
    }, 300)
    return () => clearTimeout(timer)
  }, [fetchReviews])

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this review?')) return

    try {
      await apiClient.deleteReview(id)
      toast({
        title: 'Success',
        description: 'Review deleted successfully',
      })
      fetchReviews()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete review',
        variant: 'destructive',
      })
    }
  }

  const handleEdit = (review: Review) => {
    setSelectedReview(review)
    setShowForm(true)
  }

  const handleCreate = () => {
    setSelectedReview(undefined)
    setShowForm(true)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    fetchReviews()
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Product Reviews</h1>
          <p className="text-muted-foreground">Manage customer reviews and ratings</p>
        </div>
      </div>

      <ReviewList
        reviews={reviews}
        isLoading={loading}
        searchTerm={search}
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        onSearchChange={handleSearchChange}
        onPageChange={handlePageChange}
        onAddReview={handleCreate}
        onEditReview={handleEdit}
        onDeleteReview={handleDelete}
      />

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedReview ? 'Edit Review' : 'Create Manual Review'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <ReviewForm
                review={selectedReview}
                onSuccess={handleFormSuccess}
                onCancel={() => setShowForm(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
